import React, { useState, useEffect, useCallback } from 'react';
import SiteLayout from '../../layouts/SiteLayout';
import Header from '../../components/Header/Header';
import { useAuth } from '../../context/AuthContext';
import { API_URL } from '../../config';
import PremiumLock from '../../components/Common/PremiumLock';
import useScreenType from '../../hooks/useScreenType';

// Views
import DesktopNiaPortfolio from './DesktopNiaPortfolio';
import MobileNiaPortfolio from './MobileNiaPortfolio';
import { NIA_HISTORY_MAX_DAYS } from './NiaPortfolioHistory';

const styles: Record<string, React.CSSProperties> = {
    toast: { position: 'fixed', bottom: 30, right: 30, padding: '16px 28px', borderRadius: 12, color: 'white', fontWeight: 'bold', zIndex: 10000, boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)', display: 'flex', alignItems: 'center', gap: 12, fontSize: 14 },
    modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(15, 23, 42, 0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 15000, backdropFilter: 'blur(4px)' },
    modalContent: { backgroundColor: 'white', padding: 30, borderRadius: 16, width: '90%', maxWidth: '500px', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' },
    modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 },
    modalBody: { marginBottom: 25, fontSize: 15, color: '#4b5563', lineHeight: '1.5' },
    modalFooter: { display: 'flex', justifyContent: 'flex-end', gap: 12 },
    closeBtn: { background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: '#94a3b8', lineHeight: 1 },
    cancelBtn: { padding: '10px 20px', borderRadius: 8, border: '1px solid #e2e8f0', backgroundColor: 'white', cursor: 'pointer', fontWeight: 600 },
    modalConfirmBtn: { padding: '10px 20px', borderRadius: 8, border: 'none', color: 'white', cursor: 'pointer', fontWeight: 600 },
    label: { display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 6 },
    input: { width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border-color)', backgroundColor: 'var(--bg-input)', color: 'var(--text-main)', fontSize: 14, outline: 'none' },
};

const Modal = ({ title, children, onClose, onConfirm, confirmLabel, confirmColor }: any) => (
    <div style={styles.modalOverlay}>
        <div style={styles.modalContent}>
            <div style={styles.modalHeader}>
                <h3 style={{ margin: 0 }}>{title}</h3>
                <button onClick={onClose} style={styles.closeBtn}>&times;</button>
            </div>
            <div style={styles.modalBody}>{children}</div>
            <div style={styles.modalFooter}>
                <button onClick={onClose} style={styles.cancelBtn}>Annuler</button>
                <button onClick={onConfirm} style={{ ...styles.modalConfirmBtn, backgroundColor: confirmColor || '#7c3aed' }}>
                    {confirmLabel || 'Confirmer'}
                </button>
            </div>
        </div>
    </div>
);

const NiaPortfolioScreen: React.FC = () => {
    const { token, hasPermission, user } = useAuth();
    const isAdmin = user?.role?.name === 'global_admin';
    const { isMobile, isTablet } = useScreenType();
    const useMobileLayout = isMobile || isTablet;

    const [aiPortfolio, setAiPortfolio] = useState<any>(null);
    const [totalInvested, setTotalInvested] = useState<number>(1000000);
    const [aiPositions, setAiPositions] = useState<any[]>([]);
    const [aiDecisions, setAiDecisions] = useState<any[]>([]);
    const [portfolioHistory, setPortfolioHistory] = useState<any[]>([]);
    const [aiLoading, setAiLoading] = useState(false);
    const [cycleLoading, setCycleLoading] = useState(false);

    const [toast, setToast] = useState<{ message: string, type: 'success' | 'error' } | null>(null);
    const [activeModal, setActiveModal] = useState<'reset' | 'adjust' | 'run' | null>(null);
    const [adjustForm, setAdjustForm] = useState({ amount: '', reason: '' });

    const showToast = useCallback((message: string, type: 'success' | 'error' = 'success') => {
        setToast({ message, type });
        setTimeout(() => setToast(null), 5000);
    }, []);

    const fetchAiPortfolio = useCallback(async () => {
        if (!token) return;
        setAiLoading(true);
        try {
            const [mainRes, histRes] = await Promise.all([
                fetch(`${API_URL}/api/ai-portfolio/global`, {
                    headers: { 'Accept': 'application/json', 'Authorization': `Bearer ${token}` }
                }),
                fetch(`${API_URL}/api/ai-portfolio/global/history?days=${NIA_HISTORY_MAX_DAYS}`, {
                    headers: { 'Accept': 'application/json', 'Authorization': `Bearer ${token}` }
                }),
            ]);
            if (!mainRes.ok) return;
            const data = await mainRes.json();
            setAiPortfolio({
                ...data.portfolio,
                total_fees: data.total_fees,
                current_value: data.current_value,
                performance_abs: data.performance_abs,
                performance_pct: data.performance_pct
            });
            setTotalInvested(data.total_invested || 1000000);
            setAiPositions(data.positions || []);
            setAiDecisions(data.decisions || []);
            if (histRes.ok) {
                const histData = await histRes.json();
                setPortfolioHistory(histData.history || []);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setAiLoading(false);
        }
    }, [token]);

    useEffect(() => {
        fetchAiPortfolio();
    }, [fetchAiPortfolio]);

    const handleCreateAiPortfolio = async () => {
        if (!token) return;
        setAiLoading(true);
        try {
            const res = await fetch(`${API_URL}/api/ai-portfolio/global/create`, {
                method: 'POST', headers: { Authorization: `Bearer ${token}` }
            });
            if (res.ok) {
                showToast('Portefeuille créé !');
                fetchAiPortfolio();
            } else {
                showToast('Erreur création', 'error');
            }
        } catch (e) { showToast('Erreur réseau', 'error'); } finally { setAiLoading(false); }
    };

    const handleRunAiCycleExec = async () => {
        if (!token) return;
        setCycleLoading(true);
        setActiveModal(null);
        try {
            const res = await fetch(`${API_URL}/api/ai-portfolio/global/run`, {
                method: 'POST', headers: { Authorization: `Bearer ${token}` }
            });
            if (res.ok) {
                showToast('Cycle IA lancé !');
                setTimeout(fetchAiPortfolio, 8000);
            } else { showToast('Erreur lancement', 'error'); }
        } catch (e) { showToast('Erreur réseau', 'error'); } finally { setCycleLoading(false); }
    };

    const handleResetPortfolioExec = async () => {
        if (!token) return;
        setAiLoading(true);
        setActiveModal(null);
        try {
            const res = await fetch(`${API_URL}/api/ai-portfolio/global/reset`, {
                method: 'POST', headers: { Authorization: `Bearer ${token}` }
            });
            if (res.ok) {
                showToast('Réinitialisation réussie !');
                fetchAiPortfolio();
            } else {
                const data = await res.json().catch(() => ({}));
                showToast(data.detail || 'Erreur reset', 'error');
            }
        } catch (e) { showToast('Erreur réseau', 'error'); } finally { setAiLoading(false); }
    };

    const handleAdjustCashExec = async () => {
        if (!token) return;
        const amount = parseFloat(adjustForm.amount);
        if (isNaN(amount)) { showToast('Montant invalide', 'error'); return; }
        setActiveModal(null);
        try {
            const res = await fetch(`${API_URL}/api/ai-portfolio/global/adjust-cash`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ amount, reasoning: adjustForm.reason })
            });
            if (res.ok) {
                showToast('Cash ajusté !');
                setAdjustForm({ amount: '', reason: '' });
                fetchAiPortfolio();
            } else {
                const data = await res.json().catch(() => ({}));
                showToast(data.detail || 'Erreur ajustement', 'error');
            }
        } catch (e) { showToast('Erreur réseau', 'error'); }
    };

    const isPro = hasPermission('view_advanced_ai_data') || aiPortfolio?.is_pro === true || isAdmin;

    const childProps = {
        aiPortfolio,
        totalInvested,
        aiPositions,
        aiDecisions,
        portfolioHistory,
        isAdmin,
        isPro,
        cycleLoading,
        setActiveModal,
        setAdjustForm,
        handleCreateAiPortfolio,
        aiLoading
    };

    return (
        <SiteLayout>
            <Header title="Trading IA 🤖" />

            {/* Overlay UI elements placed at root for maximum visibility */}
            {toast && (
                <div style={{
                    ...styles.toast,
                    backgroundColor: toast.type === 'success' ? '#059669' : '#dc2626',
                    bottom: useMobileLayout ? 80 : 30 // Avoid overlapping mobile bottom nav if it exists
                }}>
                    {toast.type === 'success' ? '✅' : '❌'} {toast.message}
                </div>
            )}

            {activeModal === 'run' && (
                <Modal title="⚡ Lancer Cycle IA" onClose={() => setActiveModal(null)} onConfirm={handleRunAiCycleExec} confirmLabel="Lancer l'Analyse" confirmColor="#7c3aed">
                    <p>Voulez-vous lancer l'analyse Nia ? Cette opération exécute des ordres réels sur le compte virtuel.</p>
                </Modal>
            )}

            {activeModal === 'reset' && (
                <Modal title="⚠️ Réinitialisation" onClose={() => setActiveModal(null)} onConfirm={handleResetPortfolioExec} confirmLabel="Réinitialiser" confirmColor="#dc2626">
                    <p style={{ color: '#991b1b', fontWeight: 'bold' }}>ATTENTION : Action irréversible.</p>
                    <p>Tout l'historique sera effacé et le capital remis à 1,000,000 FCFA.</p>
                </Modal>
            )}

            {activeModal === 'adjust' && (
                <Modal title="💰 Ajustement Cash" onClose={() => setActiveModal(null)} onConfirm={handleAdjustCashExec} confirmLabel="Appliquer" confirmColor="#10b981">
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
                        <div>
                            <label style={styles.label}>Montant (FCFA)</label>
                            <input type="number" style={styles.input} value={adjustForm.amount} onChange={(e) => setAdjustForm({ ...adjustForm, amount: e.target.value })} />
                        </div>
                        <div>
                            <label style={styles.label}>Raison</label>
                            <textarea style={{ ...styles.input, minHeight: 80 }} value={adjustForm.reason} onChange={(e) => setAdjustForm({ ...adjustForm, reason: e.target.value })} />
                        </div>
                    </div>
                </Modal>
            )}

            {useMobileLayout ? (
                <MobileNiaPortfolio {...childProps} />
            ) : (
                <DesktopNiaPortfolio {...childProps} />
            )}
        </SiteLayout>
    );
};

export default NiaPortfolioScreen;
